import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roleService } from "../api/services/roles/roleService";
import {
  CreateRoleRequest,
  GetRolesParams,
  UpdateRoleRequest,
} from "../api/services/roles/type";

const ROLES_STALE_TIME = Number(process.env.ROLES_STALE_TIME) || 5 * 60 * 1000; // Default to 5 minutes

export const roleKeys = {
  all: ["roles"] as const,
  details: () => [...roleKeys.all, "detail"] as const,
  detail: (id: string) => [...roleKeys.details(), id] as const,
};

export const useRole = (id: string) => {
  return useQuery({
    queryKey: roleKeys.detail(id),
    queryFn: () => roleService.getRole(id),
    enabled: !!id,
    staleTime: ROLES_STALE_TIME,
  });
};

export const useRoles = (params: GetRolesParams = {}) => {
  return useQuery({
    queryKey: ["roles", params],
    queryFn: () => roleService.getRoles(params),
    staleTime: ROLES_STALE_TIME,
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoleRequest) => roleService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      roleId,
      data,
    }: {
      roleId: string;
      data: UpdateRoleRequest;
    }) => roleService.updateRole(roleId, data),
    onSuccess: (updatedRole) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.setQueryData(roleKeys.detail(updatedRole.id), updatedRole);
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) => roleService.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};
